import React, { useMemo } from 'react';
import {
  Activity,
  ChevronLeft,
  Database,
  FileText,
  GitPullRequestArrow,
  Layers,
  ListChecks,
  ExternalLink,
  Route,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';
import { AssistantPanelShell } from '../../../components/assistant/AssistantPanelShell';
import {
  AssistantModeHeader,
  type AssistantModePickerOption,
} from '../../../core/assistant/components';
import { useAssistantPanelNav } from '../../../core/assistant/hooks/useAssistantPanelNav';
import { useFlowchartLayoutStore } from '../../../stores/flowchartLayoutStore';
import { useDiagramStore } from '../../../stores/diagramStore';
import { getCommandManager } from '../../../hooks/useCommandManager';
import { ApplyAutoLayoutCommand } from '../commands/ApplyAutoLayoutCommand';
import {
  CreateDecisionCommand,
  CreateProcessStepCommand,
  CreateSwimLaneCommand,
  CreateTerminatorCommand,
  InsertStepBetweenCommand,
  QuickCreateFlowchartNodeCommand,
} from '../commands';
import { Button } from '../../../components/ui/button';
import { SectionDisclosure } from '../../../components/ui/panel/SectionDisclosure';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import { getShapeManifest } from '../../../lib/diagram/registries';
import {
  ProcessRepositoryPanel,
  type DeletedRepositoryRecord,
  type RecordChangeMap,
} from './ProcessRepositoryPanel';
import { RepositoryCommitControls } from '../../data-binding/components';
import { useDiagramConnections } from '../../data-binding/hooks/useDiagramConnections';
import { useFolderSourceAuthorities } from '../../data-binding/hooks/useFolderSourceAuthorities';
import { useFolderSourceBindings } from '../../data-binding/hooks/useFolderSourceBindings';
import { useRepositoryCommit } from '../../data-binding/hooks/useRepositoryCommit';
import { useUncommittedSummary } from '../../data-binding/hooks/useBoundRecordStore';
import { getDeletedNodeBindings } from '../../data-binding/utils/nodeBinding';
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext';
import { useReferencingDiagrams } from '../../../hooks/useDiagramLinkHooks';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useSubProcessParentStore } from '../stores/subProcessParentStore';
import { ProcessProfile } from '../ProcessProfile';
import { Tracker } from '../../analytics/services/Tracker';
import { DATA_BINDING } from '../../analytics/constants/MetricEvents';
import { getGravityUserId } from '../../../hooks/useAuth';
import { AgentChatPanel } from '../../../core/ai/agent/components/AgentChatPanel';
import { AgentToolRegistry } from '../../../core/ai/agent/AgentToolRegistry';
import { registerCoreAgentTools } from '../../../core/ai/agent/tools';
import { registerCompassAgentTools } from '../../compass/ai/tools';
import { registerFlowchartAgentTools } from '../../flowchart/ai/tools';
import { registerProcessAgentTools } from '../ai/tools';
import { useAgent } from '../../../core/ai/agent/useAgent';
import { useViewRulesStore } from '../../../core/assistant/stores/viewRulesStore';
import { useScenariosStore } from '../../../core/assistant/stores/scenariosStore';
import { useHighlightStore } from '../../../stores/highlightStore';
import { useFolderData } from '../../../hooks/useFolderData';
import { useCompassGraph } from '../../../hooks/useCompassGraph';
import { ProcessLensPresets } from './ProcessLensPresets';
import { ProcessRecipes } from './ProcessRecipes';
import { CompassFolderResourcesSection } from '../../compass/components/panel';
import { useIsProcessRepository } from '../hooks/useIsProcessRepository';
import {
  resolveProcessSurfacePlacement,
  type ProcessAssistantMode,
  type ProcessChatSuggestionId,
  type ProcessLayer,
  type ProcessSelectedElementKind,
} from '../assistant/processSurfacePlacement';
import { ProcessModeFocusSurface } from './assistant/ProcessModeFocusSurface';
import { ProcessDurationInputAction } from './assistant/ProcessDurationInputAction';
import type { Node } from '../../../core/models/scene';
import { Logger } from '../../../utils/logger';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { resolveReviewedPrimaryConnection } from '../../data-binding/utils/resolveReviewedPrimaryConnection';

interface ProcessMappingAssistantPanelProps {
  canWrite: boolean;
  isLoadingAccess: boolean;
  initialComposePrefill?: { token: number; text: string } | null;
}

// The panel runs in two tiers, mirroring the folder-panel's basic/rich split
// (see plans/process-folder-panel-redesign.md). Casual flowchart users see
// drawing-focused suggestions and primitive actions; once any signal of
// repository work appears (Compass folder graph, connected source, or
// uncommitted record changes), the panel auto-promotes to surface
// governance/lens/inventory affordances.

// Drawing-focused chips for users who are *just drawing a flowchart*. Verbs
// here stay close to the canvas — generate, group, tidy, suggest — and avoid
// governance vocabulary that would feel alien to the casual user.
//
// The first chip is the "compose" featured affordance — clicking it
// prefills the textarea with a description template instead of sending,
// so the user can paste/describe and submit. See `handleSuggestionSelect`.
const PROCESS_CHAT_SUGGESTION_PROMPTS: Record<ProcessChatSuggestionId, string> = {
  'describe-process': 'Describe the process you want to map, including the main steps, decisions, and owners if you know them.',
  'draft-from-notes': 'Draft a process from these notes:',
  'suggest-decision-branches': 'Suggest decision branches that may be missing from this process.',
  'find-bottlenecks': 'Find bottlenecks in this process.',
  'check-compliance': 'Check this process for control coverage, evidence strength, risks, and compliance gaps.',
  'find-ownership-gaps': 'Find process steps with missing owners or unclear accountability.',
  'changed-recently': 'Summarize what changed recently in this process repository.',
  'summarize-changes': 'Summarize the pending process changes.',
  'find-evidence-gaps': 'Find evidence gaps in this process.',
  'find-decision-points': 'Find decision points that need clearer branch outcomes in this process.',
  'find-risky-changes': 'Find risky changes in the pending process updates.',
  'find-risky-handoffs': 'Highlight risky handoffs between teams or systems.',
  'create-management-brief': 'Create a management summary for this process.',
  'list-owner-actions': 'List owner actions.',
  'check-qms-readiness': 'Check this process for QMS readiness, procedure quality, evidence coverage, approvals, and audit gaps.',
  'find-procedure-gaps': 'Find missing procedure evidence, approvals, and quality records for this process.',
  'prepare-audit-walkthrough': 'Prepare an audit review walkthrough for this process, including controls, evidence, findings, and follow-up questions.',
  'suggest-useful-view': 'Suggest a useful view for this process.',
  'run-structure-scan': 'Run structure scan.',
  'find-missing-process-data': 'Find the process fields I should add for improvement analysis, including owners, systems, SLAs, and risks.',
};

const normalizeChatPrompt = (value: string): string => value.trim().toLowerCase();

const getNodeDims = (n: Node): { w: number; h: number } => ({
  w: n.w ?? n.width ?? 120,
  h: n.h ?? n.height ?? 80,
});

const centerOfNode = (n: Node): { x: number; y: number } => {
  const { w, h } = getNodeDims(n);
  return { x: (n.x ?? 0) + w / 2, y: (n.y ?? 0) + h / 2 };
};

const getFlowchartShapeDims = (variant: string): { w: number; h: number } => {
  const dimsVariant = variant === 'data' ? 'input-output' : variant;
  const manifest = getShapeManifest('flowchart', dimsVariant);
  return {
    w: manifest?.defaultDimensions?.width ?? 120,
    h: manifest?.defaultDimensions?.height ?? 80,
  };
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readString = (
  record: Record<string, unknown> | null,
  key: string,
): string | null => {
  const value = record?.[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

const readBoolean = (
  record: Record<string, unknown> | null,
  key: string,
): boolean => record?.[key] === true;

const nodeHasToken = (node: Node, tokens: readonly string[]): boolean => {
  const attrs = asRecord(node.attrs);
  const props = asRecord(node.props);
  const values = [
    node.type,
    readString(attrs, 'variant'),
    readString(attrs, 'shapeType'),
    readString(attrs, 'processNodeType'),
    readString(props, 'variant'),
    readString(props, 'shapeType'),
    readString(props, 'processNodeType'),
  ].filter((value): value is string => Boolean(value));

  return values.some((value) => {
    const normalized = value.toLowerCase();
    return tokens.some((token) => normalized.includes(token));
  });
};

const IMPROVEMENT_FIELD_KEYS = [
  'owner',
  'sla',
  'slaTrigger',
  'duration',
  'frequency',
  'triggerType',
  'systems',
  'primarySystem',
  'thirdPartyDependencies',
  'botDependencies',
  'modelDependencies',
  'inputs',
  'outputs',
  'automationStatus',
  'automationTool',
  'exceptions',
  'delegation',
  'riskLevel',
  'riskRefs',
  'escalationPath',
  'costRate',
  'fixedCost',
] as const;

const hasMeaningfulValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number' || typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return false;
};

const nodeHasProcessImprovementData = (node: Node): boolean => {
  const attrs = asRecord(node.attrs);
  const props = asRecord(node.props);
  return IMPROVEMENT_FIELD_KEYS.some(
    (key) => hasMeaningfulValue(attrs?.[key]) || hasMeaningfulValue(props?.[key]),
  );
};

const inferProcessLayer = (input: {
  metadataLayer: string | null;
  tracksCompliance: boolean;
  hasComplianceLayer: boolean;
  frameworkId: string | null;
  hasRepositoryRecords: boolean;
  hasSource: boolean;
}): ProcessLayer => {
  if (
    input.metadataLayer === 'compliance' ||
    input.metadataLayer === 'assurance' ||
    input.tracksCompliance ||
    input.hasComplianceLayer ||
    input.frameworkId
  ) {
    return input.metadataLayer === 'assurance' ? 'assurance' : 'compliance';
  }

  if (
    input.metadataLayer === 'repository' ||
    input.hasRepositoryRecords ||
    input.hasSource
  ) {
    return 'repository';
  }

  return 'diagram';
};

export const ProcessMappingAssistantPanel: React.FC<ProcessMappingAssistantPanelProps> = ({
  canWrite,
  isLoadingAccess,
  initialComposePrefill = null,
}) => {
  const { t } = useTranslation('diagram');

  const chatSuggestionLabels = React.useMemo<Record<ProcessChatSuggestionId, string>>(() => ({
    'describe-process': t('processMappingAssistant.chips.compose'),
    'draft-from-notes': t('processMappingAssistant.chips.sourceTextNotes'),
    'suggest-decision-branches': t('processMappingAssistant.chips.suggestDecisionBranches'),
    'find-bottlenecks': t('processMappingAssistant.chips.findBottlenecks'),
    'check-compliance': t('processMappingAssistant.chips.checkCompliance'),
    'find-ownership-gaps': t('processMappingAssistant.chips.findOwnershipGaps'),
    'changed-recently': t('processMappingAssistant.chips.changedRecently'),
    'summarize-changes': t('processMappingAssistant.chips.summarizeChanges', {
      defaultValue: 'Summarize changes',
    }),
    'find-evidence-gaps': t('processMappingAssistant.chips.findEvidenceGaps', {
      defaultValue: 'Find evidence gaps',
    }),
    'find-decision-points': t('processMappingAssistant.chips.findDecisionPoints', {
      defaultValue: 'Find decision points',
    }),
    'find-risky-changes': t('processMappingAssistant.chips.findRiskyChanges', {
      defaultValue: 'Find risky changes',
    }),
    'find-risky-handoffs': t('processMappingAssistant.chips.findRiskyHandoffs', {
      defaultValue: 'Find risky handoffs',
    }),
    'create-management-brief': t('processMappingAssistant.chips.createManagementBrief', {
      defaultValue: 'Create leadership brief',
    }),
    'list-owner-actions': t('processMappingAssistant.chips.listOwnerActions', {
      defaultValue: 'List owner actions',
    }),
    'check-qms-readiness': t('processMappingAssistant.chips.checkQmsReadiness', {
      defaultValue: 'Check QMS readiness',
    }),
    'find-procedure-gaps': t('processMappingAssistant.chips.findProcedureGaps', {
      defaultValue: 'Find procedure gaps',
    }),
    'prepare-audit-walkthrough': t('processMappingAssistant.chips.prepareAuditWalkthrough', {
      defaultValue: 'Prepare audit walkthrough',
    }),
    'suggest-useful-view': t('processMappingAssistant.chips.suggestUsefulView', {
      defaultValue: 'Suggest a useful view',
    }),
    'run-structure-scan': t('processMappingAssistant.chips.runStructureScan', {
      defaultValue: 'Run structure scan',
    }),
    'find-missing-process-data': t('processMappingAssistant.chips.findMissingProcessData', {
      defaultValue: 'Find missing fields',
    }),
  }), [t]);
  const chatSuggestionPromptByLabel = React.useMemo(() => {
    const entries = Object.entries(chatSuggestionLabels).map(([id, label]) => [
      label,
      PROCESS_CHAT_SUGGESTION_PROMPTS[id as ProcessChatSuggestionId] ?? label,
    ] as const);
    return new Map(entries);
  }, [chatSuggestionLabels]);

  const assistantModeOptionMap = React.useMemo<Record<ProcessAssistantMode, AssistantModePickerOption<ProcessAssistantMode>>>(() => ({
    'draw-flowchart': {
      id: 'draw-flowchart',
      label: 'Draw flowchart',
      description: 'Map steps, decisions, and layout.',
      icon: Route,
      tone: 'blue',
    },
    'draft-from-notes': {
      id: 'draft-from-notes',
      label: 'Draft from notes',
      description: 'Paste an SOP, transcript, or rough steps.',
      icon: FileText,
      tone: 'emerald',
    },
    'process-library': {
      id: 'process-library',
      label: 'Process repository',
      description: 'Review folder records, sources, and repository-backed processes.',
      icon: Database,
      tone: 'slate',
    },
    'review-imports': {
      id: 'review-imports',
      label: 'Review imports',
      description: 'Check proposed changes before applying them.',
      icon: GitPullRequestArrow,
      tone: 'amber',
    },
    'improve-process': {
      id: 'improve-process',
      label: 'Improve process',
      description: 'Find bottlenecks, risky handoffs, and owner gaps.',
      icon: Activity,
      tone: 'blue',
    },
    'management-summary': {
      id: 'management-summary',
      label: 'Management summary',
      description: 'Summarize owners, handoffs, bottlenecks, and actions.',
      icon: Activity,
      tone: 'blue',
    },
    compliance: {
      id: 'compliance',
      label: 'Compliance',
      description: 'Work with controls, evidence, risks, and gaps.',
      icon: ShieldCheck,
      tone: 'red',
    },
    qms: {
      id: 'qms',
      label: 'QMS',
      description: 'Check procedure quality, approvals, and audit readiness.',
      icon: ListChecks,
      tone: 'emerald',
    },
    'audit-review': {
      id: 'audit-review',
      label: 'Audit review',
      description: 'Prepare walkthroughs, evidence gaps, and findings.',
      icon: GitPullRequestArrow,
      tone: 'amber',
    },
    'custom-view': {
      id: 'custom-view',
      label: 'Custom view',
      description: 'Change lenses, layout, and display details.',
      icon: SlidersHorizontal,
      tone: 'slate',
    },
  }), []);

  // Compose prefill — used by the featured "Describe a process" chip and
  // by the file-drop handler. Fed into AgentChatPanel.prefillInput so the
  // textarea gets seeded; the user can edit before sending. Cleared with
  // a setTimeout right after the prefill applies so the same chip click
  // can be re-triggered later.
  const [composePrefill, setComposePrefill] = React.useState<string | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [selectedAssistantMode, setSelectedAssistantMode] = React.useState<ProcessAssistantMode | null>(null);
  const [modeMenuOpen, setModeMenuOpen] = React.useState(false);
  const {
    isProcessRepositoryEnabled,
    loading: featureFlagsLoading,
  } = useFeatureFlags();
  const processRepositoryAvailable =
    !featureFlagsLoading && isProcessRepositoryEnabled();
  const selectedIds = useDiagramStore((s) => s.selectedIds);
  const nodes = useDiagramStore((s) => s.nodes);
  const edges = useDiagramStore((s) => s.edges);

  // AI Agent setup
  const commandManager = useMemo(() => getCommandManager(), []);

  const agentRegistry = useMemo(() => {
    const registry = new AgentToolRegistry();
    registerCoreAgentTools(registry);
    registerFlowchartAgentTools(registry);
    registerProcessAgentTools(registry);
    // Compass agent tools (compliance evaluate / gaps / risks / attach
    // evidence / recommend remediation / export bundle) — exposed only
    // when this panel mounts inside a folder. See
    // docs/core/agent-tool-architecture.md and
    // plans/compass/audit-deliverable-plan.md.
    registerCompassAgentTools(registry);
    return registry;
  }, []);

  const agentStores = useMemo(() => ({
    viewRules: useViewRulesStore.getState(),
    scenarios: useScenariosStore.getState(),
    highlight: useHighlightStore.getState(),
  }), []);

  // Yjs document + diagram id from the diagram document context. The
  // panel mounts inside DiagramDocumentProvider, so both are guaranteed
  // present — no need for the legacy `window.__DIAGRAM_ID__` global.
  const { doc, diagramId } = useDiagramDocument();

  // Fetch diagram data source connections
  const {
    data: connectionsData,
    isLoading: isLoadingDiagramConnections,
  } = useDiagramConnections(diagramId);
  const { data: diagramInfo } = useDiagramInfo(diagramId);
  // Folder + Compass graph detection — drives whether the Repository section
  // surfaces the Compass folder resources (governance bridge). Same hook +
  // gating logic used by NetworkAssistantPanel.
  const currentFolderId = diagramInfo?.folderId ?? undefined;
  const { data: currentFolderData } = useFolderData(currentFolderId);
  const currentOrgId = currentFolderData?.folder?.orgId;
  const { data: folderGraph } = useCompassGraph(currentFolderId, currentOrgId);
  const hasFolderGraph = Boolean(folderGraph && folderGraph.nodes.length > 0);
  const {
    authorities: folderSourceAuthorities,
    isLoading: isLoadingSourceAuthorities,
  } = useFolderSourceAuthorities(currentFolderId, Boolean(currentFolderId));
  const {
    bindings: folderSourceBindings,
    isLoading: isLoadingSourceBindings,
  } = useFolderSourceBindings(currentFolderId);
  const primaryConnection = useMemo(() => resolveReviewedPrimaryConnection(
    connectionsData?.connections ?? [],
    folderSourceAuthorities,
    folderSourceBindings,
    'process_inventory',
  ), [connectionsData, folderSourceAuthorities, folderSourceBindings]);
  const isLoadingConnections = isLoadingDiagramConnections
    || isLoadingSourceAuthorities
    || isLoadingSourceBindings;

  const sourceId = primaryConnection?.dataSourceId;
  const folderId = diagramInfo?.folderId;

  // Agent setup must come after `folderId` so we can pass it to
  // assistantContext. Moved down from the top of the component to fix a
  // TDZ error — earlier the JSX above this point doesn't use `agent`.
  const agent = useAgent({
    registry: agentRegistry,
    diagramData: { nodes, edges, fields: [] },
    getLatestDiagramData: () => {
      const state = useDiagramStore.getState();
      return { nodes: state.nodes, edges: state.edges, fields: [] };
    },
    diagramType: 'flowchart',
    selectedNodeIds: Array.from(selectedIds),
    canWrite,
    commandManager,
    stores: agentStores,
    // Compass agent tools read folderId from assistantContext to
    // address the per-folder REST endpoints. When this panel mounts
    // outside a folder, the tools fail-fast with a friendly message.
    assistantContext: folderId ? { folderId, diagramId } : { diagramId },
  });

  const currentUserId = getGravityUserId();
  const commitRestrictedToAuthorizer =
    (primaryConnection?.dataSource?.provider === 'azure_ad' ||
      primaryConnection?.dataSource?.provider === 'google_workspace' ||
      primaryConnection?.dataSource?.provider === 'bamboohr') &&
    !!primaryConnection?.dataSource?.authorizedByUserId &&
    primaryConnection.dataSource.authorizedByUserId !== currentUserId;

  // Detect if this diagram is a sub-process target and extract parent context
  const { data: referencingLinks } = useReferencingDiagrams(diagramId ?? null);
  const setSubProcessParent = useSubProcessParentStore((s) => s.set);
  const setDiagramSourceId = useSubProcessParentStore((s) => s.setDiagramSourceId);

  // Always sync the diagram-level sourceId so shape creation can use it
  React.useEffect(() => {
    setDiagramSourceId(sourceId ?? null);
  }, [sourceId, setDiagramSourceId]);

  React.useEffect(() => {
    if (!referencingLinks || referencingLinks.length === 0) {
      setSubProcessParent(null, null);
      return;
    }
    // Find the first subprocess link with parent metadata
    const subprocessLink = referencingLinks.find(
      (link) => link.linkType === 'subprocess' && link.metadata
    );
    if (subprocessLink?.metadata) {
      const recordId = subprocessLink.metadata.parentRecordId
        ? String(subprocessLink.metadata.parentRecordId)
        : null;
      const metaSourceId = subprocessLink.metadata.parentSourceId
        ? String(subprocessLink.metadata.parentSourceId)
        : null;
      setSubProcessParent(recordId, metaSourceId);
    } else {
      setSubProcessParent(null, null);
    }
  }, [referencingLinks, setSubProcessParent]);

  // Track uncommitted changes from the centralized store
  const uncommittedSummary = useUncommittedSummary(sourceId);

  // Tier signal: any of the three repository activity signals promotes the
  // panel from basic (drawing-focused) to rich (Compass / governance / lens).
  // Single source of truth — the same hook drives ProcessPropertyPanel and
  // any future surface that needs to gate on repository activity. Casual
  // flowchart users never see the governance vocabulary unless they cross
  // this threshold.
  const isProcessRepository = useIsProcessRepository();

  const folderMetadata = React.useMemo(
    () => asRecord((currentFolderData?.folder as { metadata?: unknown } | undefined)?.metadata),
    [currentFolderData?.folder],
  );
  const processFolderConfig = React.useMemo(
    () => asRecord(folderMetadata?.processFolderConfig),
    [folderMetadata],
  );
  const processPurpose =
    readString(folderMetadata, 'processPurpose') ??
    readString(processFolderConfig, 'processPurpose') ??
    'quick_flowchart';
  const metadataLayer =
    readString(folderMetadata, 'processLayer') ??
    readString(processFolderConfig, 'processLayer');
  const tracksCompliance =
    readBoolean(folderMetadata, 'tracksCompliance') ||
    readBoolean(processFolderConfig, 'tracksCompliance');
  const hasComplianceLayer =
    readBoolean(folderMetadata, 'hasComplianceLayer') ||
    readBoolean(processFolderConfig, 'hasComplianceLayer');
  const frameworkId =
    readString(folderMetadata, 'frameworkId') ??
    readString(processFolderConfig, 'frameworkId') ??
    readString(folderMetadata, 'frameworkKey');
  const isFederated =
    readBoolean(folderMetadata, 'isFederated') ||
    readBoolean(processFolderConfig, 'isFederated') ||
    Boolean(readString(folderMetadata, 'federationId'));
  const decisionCount = React.useMemo(
    () => nodes.filter((node) => nodeHasToken(node, ['decision', 'gateway'])).length,
    [nodes],
  );
  const laneCount = React.useMemo(
    () => nodes.filter((node) => nodeHasToken(node, ['swimlane', 'swim-lane', 'lane'])).length,
    [nodes],
  );
  const hasRepositoryRecords =
    isProcessRepository || hasFolderGraph || uncommittedSummary.totalCount > 0;
  const hasImprovementData = React.useMemo(
    () =>
      hasRepositoryRecords ||
      Boolean(sourceId) ||
      laneCount > 0 ||
      nodes.some(nodeHasProcessImprovementData),
    [hasRepositoryRecords, laneCount, nodes, sourceId],
  );
  const processLayer = inferProcessLayer({
    metadataLayer,
    tracksCompliance,
    hasComplianceLayer,
    frameworkId,
    hasRepositoryRecords,
    hasSource: Boolean(sourceId),
  });
  const selectedElementKind = React.useMemo<ProcessSelectedElementKind>(() => {
    if (!selectedIds || selectedIds.size === 0) return 'none';
    if (selectedIds.size > 1) return 'multiple';

    const selectedId = Array.from(selectedIds)[0];
    const selectedNodeForMode = nodes.find((node) => node.id === selectedId);
    if (!selectedNodeForMode) return 'edge';
    if (nodeHasToken(selectedNodeForMode, ['decision', 'gateway'])) return 'decision';
    if (nodeHasToken(selectedNodeForMode, ['swimlane', 'swim-lane', 'lane'])) return 'lane';
    return 'step';
  }, [selectedIds, nodes]);
  const pendingReviewCount = uncommittedSummary.conflictCount;

  const surfacePlacement = React.useMemo(
    () =>
      resolveProcessSurfacePlacement({
        processPurpose,
        processLayer,
        folderMaturity: null,
        nodeCount: nodes.length,
        decisionCount,
        laneCount,
        hasRepositoryRecords,
        hasSource: Boolean(sourceId),
        pendingReviewCount,
        tracksCompliance,
        hasComplianceLayer,
        hasImprovementData,
        frameworkId,
        isFederated,
        processRepositoryAvailable,
        permission: canWrite ? 'editor' : 'viewer',
        selectedElementKind,
        requestedMode: selectedAssistantMode,
        hasChatMessages: agent.messages.length > 0,
      }),
    [
      processPurpose,
      processLayer,
      nodes.length,
      decisionCount,
      laneCount,
      hasRepositoryRecords,
      sourceId,
      pendingReviewCount,
      tracksCompliance,
      hasComplianceLayer,
      hasImprovementData,
      frameworkId,
      isFederated,
      processRepositoryAvailable,
      canWrite,
      selectedElementKind,
      selectedAssistantMode,
      agent.messages.length,
    ],
  );

  const assistantModeOptions = React.useMemo(
    () =>
      surfacePlacement.availableModes.map((mode) => ({
        ...assistantModeOptionMap[mode],
        label:
          mode === surfacePlacement.activeMode
            ? surfacePlacement.headerTitle
            : assistantModeOptionMap[mode].label,
        summary:
          mode === surfacePlacement.activeMode
            ? surfacePlacement.headerSummary
            : assistantModeOptionMap[mode].summary,
      })),
    [assistantModeOptionMap, surfacePlacement],
  );

  const sentChatPromptSet = React.useMemo(() => {
    const prompts = agent.messages
      .filter((message) => message.role === 'user')
      .map((message) => message.parts.find((part) => part.type === 'text'))
      .filter((part): part is { type: 'text'; text: string } => part?.type === 'text')
      .map((part) => normalizeChatPrompt(part.text));
    return new Set(prompts);
  }, [agent.messages]);

  const modeChatSuggestions = React.useMemo(
    () =>
      surfacePlacement.chatSuggestionIds
        .filter((id) => {
          const label = chatSuggestionLabels[id];
          const prompt = PROCESS_CHAT_SUGGESTION_PROMPTS[id];
          return (
            !sentChatPromptSet.has(normalizeChatPrompt(label)) &&
            !sentChatPromptSet.has(normalizeChatPrompt(prompt))
          );
        })
        .slice(0, 2)
        .map((id) => chatSuggestionLabels[id]),
    [chatSuggestionLabels, sentChatPromptSet, surfacePlacement.chatSuggestionIds],
  );

  const chatPlaceholder = React.useMemo(() => {
    switch (surfacePlacement.activeMode) {
      case 'draft-from-notes':
        return 'Ask about the draft...';
      case 'review-imports':
        return 'Ask about these changes...';
      case 'compliance':
        return 'Ask about controls...';
      case 'qms':
        return 'Ask about QMS readiness...';
      case 'audit-review':
        return 'Ask about this audit review...';
      case 'process-library':
        return 'Ask about this process repository...';
      case 'improve-process':
        return 'Ask about improvements...';
      case 'management-summary':
        return 'Ask about the management summary...';
      case 'custom-view':
        return 'Ask about this view...';
      case 'draw-flowchart':
        return 'Ask about this process...';
    }
  }, [surfacePlacement.activeMode]);

  // Repository commit operations
  const {
    commitChanges,
    pullFromRepository,
    discardChanges,
    isCommitting,
    isPulling,
    isDiscarding,
    lastCommitResult,
    lastPullResult,
  } = useRepositoryCommit({
    doc,
    sourceId,
    diagramId,
    folderId: currentFolderId,
    sourceType: 'process_inventory',
    domain: 'process',
    profile: ProcessProfile,
    fieldMappings: primaryConnection?.fieldMappings ?? [],
  });

  const [pendingDeletedRecords, setPendingDeletedRecords] = React.useState<DeletedRepositoryRecord[]>([]);

  React.useEffect(() => {
    if (!doc || !sourceId) {
      setPendingDeletedRecords([]);
      return;
    }

    const deletedMap = doc.getMap('deletedNodeBindings');
    const syncDeletedRecords = () => {
      setPendingDeletedRecords(
        getDeletedNodeBindings(doc, sourceId).map(({ binding }) => ({
          recordId: binding.recordId,
          data: binding.cachedRecord ?? {},
          source: 'local' as const,
        }))
      );
    };

    syncDeletedRecords();
    deletedMap.observe(syncDeletedRecords);
    return () => deletedMap.unobserve(syncDeletedRecords);
  }, [doc, sourceId]);

  const deletedRepositoryRecords = React.useMemo<DeletedRepositoryRecord[]>(() => {
    const byRecordId = new Map<string, DeletedRepositoryRecord>();

    for (const record of pendingDeletedRecords) {
      byRecordId.set(record.recordId, record);
    }

    for (const deletion of lastPullResult?.deletions ?? []) {
      if (!byRecordId.has(deletion.recordId)) {
        byRecordId.set(deletion.recordId, {
          recordId: deletion.recordId,
          data: deletion.lastKnownData,
          source: 'remote',
        });
      }
    }

    for (const deletion of lastCommitResult?.deletions ?? []) {
      if (!byRecordId.has(deletion.recordId)) {
        byRecordId.set(deletion.recordId, {
          recordId: deletion.recordId,
          data: deletion.lastKnownData,
          source: 'remote',
        });
      }
    }

    return Array.from(byRecordId.values());
  }, [lastCommitResult?.deletions, lastPullResult?.deletions, pendingDeletedRecords]);

  // Create a map of recordId -> change status for the repository panel.
  const recordChanges: RecordChangeMap = useMemo(() => {
    const map: RecordChangeMap = new Map();
    for (const record of uncommittedSummary.records) {
      if (record.hasConflict) {
        map.set(record.recordId, 'conflict');
      } else if (record.isNew) {
        map.set(record.recordId, 'new');
      } else {
        map.set(record.recordId, 'modified');
      }
    }
    for (const record of deletedRepositoryRecords) {
      map.set(record.recordId, 'deleted');
    }
    return map;
  }, [deletedRepositoryRecords, uncommittedSummary.records]);

  // Tidy the entire diagram in one shot — feeds ApplyAutoLayoutCommand
  // with no targetNodeIds, which lays out every node.  Same algorithm
  // the editor's auto-layout uses; surfaced as a one-click affordance
  // because the layout was already there, just not exposed as "tidy".
  const handleTidyAll = React.useCallback(async () => {
    if (!canWrite || isLoadingAccess) return;
    const latest = useFlowchartLayoutStore.getState();
    await getCommandManager().execute(
      new ApplyAutoLayoutCommand({
        options: { strategy: latest.layoutStrategy, nodeSpacing: 80, rankSpacing: 100 },
      })
    );
  }, [canWrite, isLoadingAccess]);

  const selectedNodeIds = React.useMemo(() => {
    if (!selectedIds || selectedIds.size === 0) return [];
    const nodeIdSet = new Set(nodes.map((n) => n.id));
    return Array.from(selectedIds).filter((id) => nodeIdSet.has(id));
  }, [selectedIds, nodes]);

  const selectedNode = selectedNodeIds.length === 1 ? nodes.find((n) => n.id === selectedNodeIds[0]) ?? null : null;

  // Process-domain edges carry types outside the core EdgeType union
  // (sequence_flow / message_flow / data_association). The cast is via
  // `string`, not `any`, so all other edge fields stay typed.
  const sequenceEdges = React.useMemo(
    () => edges.filter((e) => (e.type as string) === 'sequence_flow'),
    [edges],
  );

  const incoming = React.useMemo(
    () => (selectedNode ? sequenceEdges.filter((e) => e.to === selectedNode.id) : []),
    [sequenceEdges, selectedNode],
  );

  const outgoing = React.useMemo(
    () => (selectedNode ? sequenceEdges.filter((e) => e.from === selectedNode.id) : []),
    [sequenceEdges, selectedNode],
  );

  const getViewportCenter = () => {
    const stageState = useDiagramStore.getState().stage ?? { scale: 1, offsetX: 0, offsetY: 0 };
    const scale = typeof stageState.scale === 'number' ? stageState.scale : 1;
    const offsetX = typeof stageState.offsetX === 'number' ? stageState.offsetX : 0;
    const offsetY = typeof stageState.offsetY === 'number' ? stageState.offsetY : 0;

    const container =
      typeof document !== 'undefined'
        ? document.querySelector<HTMLElement>('[data-testid="tid.canvas.stage"]')
        : null;
    const widthPx = container?.clientWidth ?? 1200;
    const heightPx = container?.clientHeight ?? 800;

    return {
      x: (widthPx / 2 - offsetX) / scale,
      y: (heightPx / 2 - offsetY) / scale,
    };
  };

  type FlowchartVariant = 'process' | 'decision' | 'start-end' | 'data' | 'document';

  const handleAddSwimLane = React.useCallback(async () => {
    if (!canWrite || isLoadingAccess) return;
    const center = getViewportCenter();
    // Lane direction follows the diagram's layout strategy: top-to-bottom
    // flowcharts get vertical lanes (header row up top, swim lanes flowing
    // downward into columns); left-to-right gets horizontal lanes (header
    // column on the left, swim lanes flowing rightward into rows).
    const layout = useFlowchartLayoutStore.getState();
    const direction = layout.layoutStrategy === 'top-to-bottom' ? 'vertical' : 'horizontal';
    // Default schema dimensions (tableSchemas.registry.ts):
    //   horizontal: 3 rows × (40 + 440) = 480w × 600h
    //   vertical:   1 row × (3 × 220) = 660w × (40 + 340) = 380h
    // Center the pool around the viewport midpoint.
    const w = direction === 'horizontal' ? 480 : 660;
    const h = direction === 'horizontal' ? 600 : 380;
    const cmd = new CreateSwimLaneCommand({
      x: center.x - w / 2,
      y: center.y - h / 2,
      direction,
    });
    await getCommandManager().execute(cmd);
  }, [canWrite, isLoadingAccess]);

  const handleStartNewFlow = React.useCallback(async (variant: FlowchartVariant) => {
    if (!canWrite || isLoadingAccess) return;
    const center = getViewportCenter();
    const parentCtx = useSubProcessParentStore.getState();
    const { w, h } = getFlowchartShapeDims(variant);
    const x = center.x - w / 2;
    const y = center.y - h / 2;
    let cmd;
    if (variant === 'process') {
      cmd = new CreateProcessStepCommand({
        x,
        y,
        width: w,
        height: h,
        dataSourceId: parentCtx.parentSourceId ?? sourceId,
        parentRecordId: parentCtx.parentRecordId ?? undefined,
      });
    } else if (variant === 'decision') {
      cmd = new CreateDecisionCommand({ x, y, width: w, height: h });
    } else if (variant === 'start-end') {
      cmd = new CreateTerminatorCommand({ x, y, width: w, height: h });
    } else {
      cmd = new CreateShapeCommand({
        type: variant,
        category: 'flowchart',
        x,
        y,
        width: w,
        height: h,
      });
    }
    await getCommandManager().execute(cmd);
  }, [canWrite, isLoadingAccess, sourceId]);

  const handleAddAtEnd = React.useCallback(async (variant: FlowchartVariant) => {
    if (!canWrite || isLoadingAccess || !selectedNode) return;
    const latest = useFlowchartLayoutStore.getState();
    const parentCtx = useSubProcessParentStore.getState();
    const direction: 'top' | 'right' | 'bottom' | 'left' = latest.layoutStrategy === 'top-to-bottom' ? 'bottom' : 'right';
    const { w, h } = getNodeDims(selectedNode);
    const cmd = new QuickCreateFlowchartNodeCommand({
      sourceNodeId: selectedNode.id,
      variant,
      direction,
      sourceX: selectedNode.x ?? 0,
      sourceY: selectedNode.y ?? 0,
      sourceW: w,
      sourceH: h,
      dataSourceId: parentCtx.parentSourceId ?? sourceId,
      parentRecordId: parentCtx.parentRecordId ?? undefined,
    });
    await getCommandManager().execute(cmd);
  }, [canWrite, isLoadingAccess, selectedNode, sourceId]);

  const insertAlongEdge = React.useCallback(async (variant: FlowchartVariant, edgeId: string) => {
    const store = useDiagramStore.getState();
    const edge = store.edges.find((e) => e.id === edgeId);
    if (!edge) return;
    const fromNode = store.nodes.find((n) => n.id === edge.from);
    const toNode = store.nodes.find((n) => n.id === edge.to);
    if (!fromNode || !toNode) return;
    const a = centerOfNode(fromNode);
    const b = centerOfNode(toNode);
    const cmd = new InsertStepBetweenCommand({
      edgeId,
      variant,
      midpointX: (a.x + b.x) / 2,
      midpointY: (a.y + b.y) / 2,
    });
    await getCommandManager().execute(cmd);
  }, []);

  const handleInsertBefore = React.useCallback(async (variant: FlowchartVariant) => {
    if (!canWrite || isLoadingAccess || !selectedNode || incoming.length === 0) return;
    await insertAlongEdge(variant, incoming[0]!.id);
  }, [canWrite, isLoadingAccess, selectedNode, incoming, insertAlongEdge]);

  const handleInsertAfter = React.useCallback(async (variant: FlowchartVariant) => {
    if (!canWrite || isLoadingAccess || !selectedNode || outgoing.length === 0) return;
    await insertAlongEdge(variant, outgoing[0]!.id);
  }, [canWrite, isLoadingAccess, selectedNode, outgoing, insertAlongEdge]);

  const handleAddEnd = React.useCallback(async () => {
    if (!canWrite || isLoadingAccess || !selectedNode || outgoing.length === 0) return;
    const latest = useFlowchartLayoutStore.getState();
    const direction: 'top' | 'right' | 'bottom' | 'left' = latest.layoutStrategy === 'top-to-bottom' ? 'bottom' : 'right';
    const { w, h } = getNodeDims(selectedNode);
    const cmd = new QuickCreateFlowchartNodeCommand({
      sourceNodeId: selectedNode.id,
      variant: 'start-end',
      direction,
      sourceX: selectedNode.x ?? 0,
      sourceY: selectedNode.y ?? 0,
      sourceW: w,
      sourceH: h,
    });
    await getCommandManager().execute(cmd);
  }, [canWrite, isLoadingAccess, selectedNode, outgoing.length]);

  // Quick actions are intentionally narrow: process-shape primitives only.
  // Higher-value affordances (lens / repository) live in their own sections.
  type QuickAction = {
    id: string;
    label: string;
    key: string;
    action: () => Promise<void>;
    disabled?: boolean;
    title?: string;
  };

  const quickActions: QuickAction[] = React.useMemo(() => {
    if (!selectedNode) {
      return [
        { id: 'add-process', label: t('processMappingAssistant.quickActions.addProcess'), key: '1', action: () => handleStartNewFlow('process') },
        { id: 'add-decision', label: t('processMappingAssistant.quickActions.addDecision'), key: '2', action: () => handleStartNewFlow('decision') },
        {
          id: 'add-swim-lane',
          label: t('processMappingAssistant.quickActions.addSwimLane'),
          key: '3',
          action: handleAddSwimLane,
          title: t('processMappingAssistant.quickActions.addSwimLaneTitle'),
        },
        { id: 'tidy-layout', label: t('processMappingAssistant.quickActions.tidyLayout'), key: '4', action: handleTidyAll },
      ];
    }

    // With selection: Show relevant actions based on node connections
    const actions: QuickAction[] = [];
    let keyCounter = 1;

    // Always available: Add Decision, Add Process
    actions.push(
      { id: 'add-decision', label: t('processMappingAssistant.quickActions.addDecision'), key: String(keyCounter++), action: () => handleAddAtEnd('decision') },
      { id: 'add-process', label: t('processMappingAssistant.quickActions.addProcess'), key: String(keyCounter++), action: () => handleAddAtEnd('process') }
    );

    // Insert Decision Before: Only if there's an incoming edge
    if (incoming.length > 0) {
      actions.push({ id: 'insert-decision-before', label: t('processMappingAssistant.quickActions.insertDecisionBefore'), key: String(keyCounter++), action: () => handleInsertBefore('decision') });
    }

    // Insert Process After: Only if there's an outgoing edge
    if (outgoing.length > 0) {
      actions.push({ id: 'insert-process-after', label: t('processMappingAssistant.quickActions.insertProcessAfter'), key: String(keyCounter++), action: () => handleInsertAfter('process') });
    }

    // Add End: Only if there's an outgoing edge
    if (outgoing.length > 0) {
      actions.push({ id: 'add-end', label: t('processMappingAssistant.quickActions.addEnd'), key: String(keyCounter++), action: handleAddEnd });
    }

    // Tidy applies to the whole diagram — always available, last numbered slot.
    actions.push({ id: 'tidy-layout', label: t('processMappingAssistant.quickActions.tidyLayout'), key: String(keyCounter++), action: handleTidyAll });

    return actions;
  }, [t, selectedNode, handleStartNewFlow, handleAddSwimLane, handleAddAtEnd, handleInsertBefore, handleInsertAfter, handleAddEnd, handleTidyAll, incoming.length, outgoing.length]);

  const navQuickActions = React.useMemo(
    () =>
      quickActions
        .filter((action) => !action.disabled)
        .map((action) => ({ id: action.id, label: action.label, key: action.key, action: action.action })),
    [quickActions],
  );

  const {
    isOpen,
    setIsOpen,
    activeSubPanel,
    navigateToSubPanel,
    navigateBack,
    getAnimationClass,
    executingActionId,
    executeQuickAction,
  } = useAssistantPanelNav<'process-library' | 'view-display'>({ quickActions: navQuickActions });

  React.useEffect(() => {
    const onCollapse = () => {
      setIsOpen(false);
    };
    const onExpand = () => setIsOpen(true);
    window.addEventListener('assistant:collapse', onCollapse);
    window.addEventListener('assistant:expand', onExpand);
    return () => {
      window.removeEventListener('assistant:collapse', onCollapse);
      window.removeEventListener('assistant:expand', onExpand);
    };
  }, [setIsOpen]);

  React.useEffect(() => {
    if (!initialComposePrefill?.text) return;
    setComposePrefill(initialComposePrefill.text);
    setIsOpen(true);
  }, [initialComposePrefill, setIsOpen]);

  // Commit/pull/discard handlers wired to useRepositoryCommit hook
  const handleCommit = React.useCallback(async () => {
    if (!diagramId || !sourceId) return;
    if (commitRestrictedToAuthorizer) return;
    Tracker.track(DATA_BINDING.COMMIT_TRIGGERED, {
      domain: 'process',
      diagramId,
      sourceId,
      uncommittedCount: uncommittedSummary.totalCount,
    });
    const result = await commitChanges('all');
    if (result.success) {
      Tracker.track(DATA_BINDING.COMMIT_COMPLETED, {
        domain: 'process',
        diagramId,
        sourceId,
      });
    } else {
      Tracker.track(DATA_BINDING.COMMIT_FAILED, {
        domain: 'process',
        diagramId,
        sourceId,
        errorCount: result.errors.length,
      });
      Logger.error('[ProcessMappingAssistant] Commit failed:', result.errors);
    }
  }, [diagramId, sourceId, commitChanges, uncommittedSummary.totalCount, commitRestrictedToAuthorizer]);

  const handlePull = React.useCallback(async () => {
    if (!diagramId || !sourceId) return;
    Tracker.track(DATA_BINDING.PULL_TRIGGERED, {
      domain: 'process',
      diagramId,
      sourceId,
    });
    const result = await pullFromRepository();
    if (result.success) {
      Tracker.track(DATA_BINDING.PULL_COMPLETED, {
        domain: 'process',
        diagramId,
        sourceId,
      });
    } else {
      Tracker.track(DATA_BINDING.PULL_FAILED, {
        domain: 'process',
        diagramId,
        sourceId,
        errorCount: result.errors.length,
      });
      Logger.error('[ProcessMappingAssistant] Pull failed:', result.errors);
    }
  }, [diagramId, sourceId, pullFromRepository]);

  const handleDiscardAll = React.useCallback(async () => {
    if (!diagramId || !sourceId) return;
    Tracker.track(DATA_BINDING.DISCARD_TRIGGERED, {
      domain: 'process',
      diagramId,
      sourceId,
      uncommittedCount: uncommittedSummary.totalCount,
    });
    const result = await discardChanges('all');
    if (result.errors.length > 0) {
      Tracker.track(DATA_BINDING.DISCARD_FAILED, {
        domain: 'process',
        diagramId,
        sourceId,
        errorCount: result.errors.length,
      });
      Logger.error('[ProcessMappingAssistant] Discard errors:', result.errors);
    } else {
      Tracker.track(DATA_BINDING.DISCARD_COMPLETED, {
        domain: 'process',
        diagramId,
        sourceId,
      });
    }
  }, [diagramId, sourceId, discardChanges, uncommittedSummary.totalCount]);

  // Global keyboard shortcuts for repository operations
  React.useEffect(() => {
    if (!sourceId || !canWrite || isLoadingAccess) return;

    const isTypingInInput = (target: EventTarget | null): boolean => {
      if (!target) return false;
      const el = target as Element;
      const tag = el.tagName?.toLowerCase?.() ?? '';
      if (tag === 'input' || tag === 'textarea') return true;
      return el.getAttribute?.('contenteditable') === 'true';
    };

    const shortcutHandler = (e: KeyboardEvent) => {
      if (isTypingInInput(e.target)) return;
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdKey = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + S = Commit changes
      if (cmdKey && e.key === 's' && !e.shiftKey && uncommittedSummary.hasChanges) {
        e.preventDefault();
        e.stopPropagation();
        if (!isCommitting && !uncommittedSummary.hasConflicts) {
          handleCommit();
        }
        return;
      }

      // Cmd/Ctrl + Shift + R = Pull from repository
      if (cmdKey && e.shiftKey && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        e.stopPropagation();
        if (!isPulling) {
          handlePull();
        }
        return;
      }
    };

    window.addEventListener('keydown', shortcutHandler, { capture: true });
    return () => window.removeEventListener('keydown', shortcutHandler, { capture: true });
  }, [
    sourceId,
    canWrite,
    isLoadingAccess,
    isCommitting,
    isPulling,
    uncommittedSummary.hasChanges,
    uncommittedSummary.hasConflicts,
    handleCommit,
    handlePull,
  ]);

  const handleAssistantModeChange = React.useCallback((mode: ProcessAssistantMode) => {
    setSelectedAssistantMode(mode);
  }, []);

  const handleDraftFromNotes = React.useCallback(() => {
    setSelectedAssistantMode('draft-from-notes');
    setComposePrefill(t('processMappingAssistant.prefills.sourceText'));
  }, [t]);

  const handleOpenReviewImports = React.useCallback(() => {
    setSelectedAssistantMode('review-imports');
    if (processRepositoryAvailable) {
      navigateToSubPanel('process-library');
    }
  }, [navigateToSubPanel, processRepositoryAvailable]);

  const handleAskPrompt = React.useCallback(async (prompt: string) => {
    await agent.sendMessage(prompt);
  }, [agent]);

  // Featured-chip behaviour. AgentChatPanel calls this for every chip
  // click when `onSuggestionSelect` is provided. The compose chip
  // *prefills* the textarea instead of sending; everything else falls
  // through to the normal sendMessage path.
  const handleSuggestionSelect = React.useCallback(async (text: string) => {
    if (text === t('processMappingAssistant.chips.compose')) {
      setComposePrefill(t('processMappingAssistant.prefills.compose'));
      return;
    }
    // "Generate steps from notes / an interview" imply source text the chip
    // can't carry — sending the bare label gives the model nothing to work
    // from. Prefill the textarea instead so the user pastes the transcript.
    if (
      text === t('processMappingAssistant.chips.sourceTextNotes') ||
      text === t('processMappingAssistant.chips.sourceTextInterview')
    ) {
      setComposePrefill(t('processMappingAssistant.prefills.sourceText'));
      return;
    }
    await agent.sendMessage(chatSuggestionPromptByLabel.get(text) ?? text);
  }, [agent, chatSuggestionPromptByLabel, t]);

  // File-drop handler. Text / markdown / CSV content is read into the textarea
  //     (prefill) so the user can preview + edit + submit. The compose
  //     prompt wraps the content with a clear "generate from this" frame.
  // Structured source ingestion stays in Folder Sources, so dropping CSV here
  // drafts a diagram and never creates diagram-owned source authority.
  // PDF / unknown: surface a hint via the prefill saying paste the
  //     text instead. Real PDF parsing is a follow-up.
  const handleFilesDropped = React.useCallback(async (files: FileList) => {
    if (files.length === 0) return;
    const file = files[0]!;
    const name = file.name.toLowerCase();
    const isPdf = name.endsWith('.pdf') || file.type === 'application/pdf';
    const isText = file.type.startsWith('text/') || name.endsWith('.md') || name.endsWith('.txt');

    if (isPdf) {
      setComposePrefill(t('processMappingAssistant.prefills.pdfUnsupported'));
      return;
    }

    if (isText || file.size < 200_000) {
      try {
        const text = await file.text();
        const trimmed = text.trim().slice(0, 8000);
        setComposePrefill(t('processMappingAssistant.prefills.fromText', { content: trimmed }));
      } catch (err) {
        Logger.warn('[ProcessMappingAssistant] Failed to read dropped file', err);
      }
      return;
    }

    setComposePrefill(t('processMappingAssistant.prefills.dropHint'));
  }, [t]);

  // Clear the prefill once AgentChatPanel reports it has applied it. Lets
  // re-clicking the same compose chip / re-dropping the same file fire the
  // prefill again — necessary because AgentChatPanel dedupes identical
  // strings against its `lastAppliedPrefillRef`.
  const handlePrefillConsumed = React.useCallback(() => {
    setComposePrefill(null);
  }, []);

  return (
    <>
    <AssistantPanelShell
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      toggleLabel={t('processMappingAssistant.toggleLabel')}
      toggleIcon={
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="shrink-0"
        >
          <path d="M23.3333 2.60217C23.586 3.12552 23.8981 3.61098 24.2679 4.04163C24.6816 4.5233 25.157 4.92948 25.678 5.24475C25.1572 5.56272 24.6814 5.97035 24.2679 6.45374C23.898 6.88607 23.5863 7.37342 23.3333 7.89807C23.0804 7.37342 22.7686 6.88607 22.3987 6.45374C21.985 5.97013 21.5087 5.56278 20.9876 5.24475C21.5089 4.92942 21.9849 4.52352 22.3987 4.04163C22.7686 3.61098 23.0807 3.12552 23.3333 2.60217Z" stroke="white"/>
          <path d="M13.043 4.56122C13.8216 6.55404 14.9759 8.37086 16.4435 9.9014C17.9255 11.4469 19.6903 12.6611 21.6284 13.4753C19.6878 14.2925 17.9204 15.5083 16.4334 17.0537C14.9604 18.5848 13.7985 20.4015 13.0106 22.3952C12.23 20.3987 11.0748 18.5777 9.60727 17.0413C8.12573 15.4902 6.36237 14.2683 4.42435 13.444C6.36579 12.6369 8.13533 11.4294 9.6232 9.889C11.0963 8.3638 12.2572 6.55121 13.043 4.56122Z" stroke="white"/>
        </svg>
      }
      toggleAriaLabel={t('processMappingAssistant.toggleAriaOpen')}
      toggleTooltip={t('processMappingAssistant.toggleAriaOpen')}
      toggleTestId="tid.process-mapping.assistant.toggle"
      panelAriaLabel={t('processMappingAssistant.panelAriaLabel')}
      panelClassName={`w-[320px] ${
        agent.messages.length > 0 && !activeSubPanel ? 'h-[calc(100vh-120px)]' : ''
      } max-h-[calc(100vh-120px)]`}
    >
      <div className="w-full flex flex-col flex-1 min-h-0">
        {/* Header — only show on home panel */}
        {!activeSubPanel && (
          <AssistantModeHeader
            panelTitle={t('processMappingAssistant.title')}
            value={surfacePlacement.activeMode}
            options={assistantModeOptions}
            onValueChange={handleAssistantModeChange}
            menuOpen={modeMenuOpen}
            onMenuOpenChange={setModeMenuOpen}
            onClose={() => setIsOpen(false)}
            ariaLabel="Change process assistant mode"
            closeAriaLabel={t('processMappingAssistant.closeAriaLabel')}
            testId="tid.process-mapping.assistant.mode-header"
          />
        )}

        {/* Sub-panel header — show on detail views */}
        {activeSubPanel && (
          <div className="px-3 py-2 border-b border-border bg-background shrink-0 flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={navigateBack}
              className="h-7 w-7 p-0 hover:bg-muted shrink-0"
              aria-label={t('processMappingAssistant.back')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold text-foreground">
              {activeSubPanel === 'process-library' && t('processMappingAssistant.processLibrary')}
              {activeSubPanel === 'view-display' &&
                t('processMappingAssistant.sections.viewsAndLenses', {
                  defaultValue: 'Views & lenses',
                })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-6 w-6 p-0 hover:bg-muted ml-auto shrink-0"
              aria-label={t('processMappingAssistant.close')}
            >
              ✕
            </Button>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* ─── SUB-PANEL: Process Repository ─── */}
          {activeSubPanel === 'process-library' && (
            <div className={`flex-1 min-h-0 flex flex-col overflow-hidden ${getAnimationClass(true)}`}>
              {isLoadingConnections ? (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">{t('processMappingAssistant.repository.loading')}</p>
                </div>
              ) : !folderId ? (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                  <Database className="h-10 w-10 text-muted-foreground mb-4" />
                  <p className="text-sm font-medium text-foreground">
                    {t('processMappingAssistant.repository.folderSourcesTitle', {
                      defaultValue: 'Sources are managed in the folder',
                    })}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('processMappingAssistant.repository.folderSourcesUnavailable', {
                      defaultValue: 'Attach this diagram to a folder to manage process sources and primary authority.',
                    })}
                  </p>
                </div>
              ) : (
                <>
                  <ProcessRepositoryPanel
                    sourceId={sourceId}
                    folderId={folderId}
                    recordChanges={recordChanges}
                    deletedRecords={deletedRepositoryRecords}
                    className="flex-1 min-h-0"
                  />
                  {/*
                    Folder Resources (graph type breakdown) lives here,
                    one drill-in away from the main panel. Collapsed by
                    default — most users only need the curated Process
                    list above; governance/power users open this for
                    the full ontology view (Activity, Actor, Control,
                    Risk, …).
                  */}
                  {hasFolderGraph && (
                    <details className="shrink-0 border-t border-border bg-background">
                      <summary className="flex cursor-pointer items-center gap-1.5 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:bg-muted/50">
                        <Layers className="h-3 w-3" />
                        {t('processMappingAssistant.folderResources')}
                      </summary>
                      <div className="max-h-[40vh] overflow-y-auto px-3 pb-3">
                        <CompassFolderResourcesSection
                          folderId={currentFolderId}
                          orgId={currentOrgId}
                        />
                      </div>
                    </details>
                  )}
                  <div className="shrink-0 border-t border-border bg-background p-2">
                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                      className="w-full justify-between"
                      data-testid="tid.process.repository.open-folder-sources"
                    >
                      <a
                        href={`/dashboard/${encodeURIComponent(folderId)}/sources`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('processMappingAssistant.repository.openFolderSources', {
                          defaultValue: 'Open Folder sources',
                        })}
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                  {sourceId && (
                    <div className="p-3 border-t border-border bg-background shrink-0">
                      <RepositoryCommitControls
                        uncommittedCount={uncommittedSummary.totalCount}
                        newRecordCount={uncommittedSummary.newRecordCount}
                        conflictCount={uncommittedSummary.conflictCount}
                        hasChanges={uncommittedSummary.hasChanges}
                        hasConflicts={uncommittedSummary.hasConflicts}
                        isCommitting={isCommitting}
                        isPulling={isPulling}
                        isDiscarding={isDiscarding}
                        onCommit={handleCommit}
                        onPull={handlePull}
                        onDiscardAll={handleDiscardAll}
                        disabled={!canWrite || isLoadingAccess}
                        commitRestricted={commitRestrictedToAuthorizer}
                        provider={primaryConnection?.dataSource?.provider}
                        sourceTypeLabel="Process inventory"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ─── SUB-PANEL: Views & lenses ─── */}
          {activeSubPanel === 'view-display' && (
            <div className={`flex-1 overflow-y-auto px-4 py-3 ${getAnimationClass(true)}`}>
              <div className="space-y-4">
                {/* Views gallery — the one-click composite views (recipes). */}
                <div>
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('assistant.views', { defaultValue: 'Views' })}
                  </div>
                  <ProcessRecipes />
                </div>

                {/* Lens chips — one-click field colourings. */}
                <div>
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('processMappingAssistant.sections.lens')}
                  </div>
                  <ProcessLensPresets tier={isProcessRepository ? 'rich' : 'basic'} />
                </div>
              </div>
            </div>
          )}

          {/* ─── HOME PANEL ─── */}
          {!activeSubPanel && (
            <div
              data-testid="tid.process-mapping.assistant.home"
              className={`flex-1 min-h-0 overflow-y-auto assistant-thin-scrollbar ${getAnimationClass(false)}`}
            >
              <div className="px-3 pt-2 pb-1">
                <ProcessModeFocusSurface
                  mode={surfacePlacement.activeMode}
                  root={surfacePlacement.root}
                  nodeCount={nodes.length}
                  decisionCount={decisionCount}
                  laneCount={laneCount}
                  pendingReviewCount={pendingReviewCount}
                  hasSource={Boolean(sourceId)}
                  hasRepositoryRecords={hasRepositoryRecords}
                  processRepositoryAvailable={processRepositoryAvailable}
                  hasImprovementData={hasImprovementData}
                  canWrite={canWrite && !isLoadingAccess}
                  onTidy={handleTidyAll}
                  onDraftFromNotes={handleDraftFromNotes}
                  onOpenProcessLibrary={() => navigateToSubPanel('process-library')}
                  onOpenReviewImports={handleOpenReviewImports}
                  onOpenViewDisplay={() => navigateToSubPanel('view-display')}
                  onAsk={handleAskPrompt}
                />
              </div>

              {/* AI Chat — compact root block. The container handles file
                  drops in basic mode (text → prefill, CSV → repository wizard). */}
              <div className="min-w-0 px-3 pt-2">
                <div
                  className={`min-h-[132px] max-h-[36vh] flex flex-col relative transition-colors ${
                    isDragOver ? 'bg-primary/5 ring-2 ring-primary/30 rounded-md' : ''
                  }`}
                  onDragEnter={(e) => {
                    if (Array.from(e.dataTransfer.types).includes('Files')) {
                      e.preventDefault();
                      setIsDragOver(true);
                    }
                  }}
                  onDragOver={(e) => {
                    if (Array.from(e.dataTransfer.types).includes('Files')) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                    }
                  }}
                  onDragLeave={(e) => {
                    if (e.currentTarget === e.target) setIsDragOver(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragOver(false);
                    if (e.dataTransfer.files.length > 0) {
                      void handleFilesDropped(e.dataTransfer.files);
                    }
                  }}
                >
                  {isDragOver && (
                    <div className="pointer-events-none absolute inset-2 rounded-md border-2 border-dashed border-primary/60 bg-primary/5 flex items-center justify-center text-[12px] font-medium text-primary z-10">
                      {t('processMappingAssistant.dropFileOverlay')}
                    </div>
                  )}
                  <AgentChatPanel
                    renderToolResultAction={(toolName, result) => toolName === 'process_critical_path'
                      ? <ProcessDurationInputAction result={result} canWrite={canWrite && !isLoadingAccess} />
                      : null}
                    messages={agent.messages}
                    isLoading={agent.isLoading}
                    loadingTrace={agent.loadingTrace}
                    liveThinkingText={agent.liveThinkingText}
                    activePreviewMessageId={agent.pendingPreviewTurnId}
                    onSend={agent.sendMessage}
                    onSuggestionSelect={handleSuggestionSelect}
                    onCancel={agent.cancel}
                    onClear={agent.clearMessages}
                    onConfirmPreview={agent.confirmPendingPreview}
                    onRejectPreview={agent.rejectPendingPreview}
                    isEmptyDiagram={false}
                    suggestions={modeChatSuggestions}
                    prefillInput={composePrefill}
                    onPrefillConsumed={handlePrefillConsumed}
                    hideEmptyStateHeader
                    emptyStatePlaceholder={chatPlaceholder}
                    suggestionsPlaceholder={chatPlaceholder}
                    compact
                  />
                </div>
              </div>

              {/* Sections below chat stay tucked/contextual; the mode surface
                  above owns the primary job for the current process state. */}
              <div className="mt-1">
                {/* Views + Lens now live in the Views & lenses sub-panel,
                    reached via the View Box "Change view" — root stays compact. */}
                {surfacePlacement.processLibrary !== 'tucked' && surfacePlacement.processLibrary !== 'absent' ? (
                  <>
                    {/* REPOSITORY — single, scannable nav row.
                       The inline Folder Resources tree used to live here
                       too, but mixing a nav button with a dense data
                       tree made the section feel ambiguous (which one
                       is the entry point?). Folder Resources now lives
                       inside the Process Repository subpanel as a
                       collapsed power-user section. */}
                    <SectionDisclosure
                      id="process-mapping.home.repository"
                      title={t('processMappingAssistant.sections.repository')}
                      defaultOpen={false}
                      compact
                    >
                      <div className="space-y-0.5">
                        <button
                          onClick={() => navigateToSubPanel('process-library')}
                          className="w-full flex items-center gap-3 px-2 py-2 text-sm rounded hover:bg-muted transition-colors text-left"
                        >
                          <Database className="shrink-0 w-5 h-5 p-0.5 text-muted-foreground" />
                          <span className="flex-1 text-sm text-foreground">
                            {t('processMappingAssistant.processLibrary')}
                            {uncommittedSummary.hasChanges && (
                              <span className="ml-1.5 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                                {uncommittedSummary.totalCount}
                              </span>
                            )}
                          </span>
                        </button>
                      </div>
                    </SectionDisclosure>
                  </>
                ) : null}

                {/* QUICK ACTIONS — primary affordance in basic mode (open),
                    tucked behind the mode surface so drawing primitives remain
                    available without competing with the active job. */}
                {surfacePlacement.quickActions !== 'absent' ? (
                  <SectionDisclosure
                    id="process-mapping.home.actions"
                    title={t('processMappingAssistant.sections.quickActions')}
                    defaultOpen={false}
                    compact
                  >
                    {quickActions.length > 0 ? (
                      <div className="space-y-0.5">
                        {quickActions.map((action) => {
                          const disabled = action.disabled || !canWrite || isLoadingAccess || !!executingActionId;
                          return (
                            <button
                              key={action.id}
                              onClick={() => {
                                if (disabled) return;
                                void executeQuickAction(action.id, action.action);
                              }}
                              disabled={disabled}
                              title={action.title}
                              className="w-full flex items-center gap-3 px-2 py-2 text-sm rounded hover:bg-muted transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                              aria-label={t('processMappingAssistant.quickActions.actionAriaLabel', { label: action.label, key: action.key })}
                              data-testid={`tid.process-mapping.quick-action.${action.id}`}
                            >
                              <kbd className="shrink-0 w-5 h-5 flex items-center justify-center text-[10px] font-semibold bg-muted rounded border border-border text-muted-foreground">
                                {action.key}
                              </kbd>
                              <span className="flex-1 text-sm text-foreground">{action.label}</span>
                              {action.disabled && (
                                <span className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">{t('processMappingAssistant.soon')}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    <div className="mt-2 pt-2 border-t border-border/50 space-y-0.5">
                      <button
                        onClick={() => navigateToSubPanel('view-display')}
                        className="w-full flex items-center gap-3 px-2 py-2 text-sm rounded hover:bg-muted transition-colors text-left"
                      >
                        <span className="shrink-0 w-5 h-5 flex items-center justify-center text-muted-foreground">⚙</span>
                        <span className="flex-1 text-sm text-foreground">
                          {t('processMappingAssistant.openViewsAndLenses', {
                            defaultValue: 'Open views & lenses',
                          })}
                        </span>
                      </button>
                    </div>
                  </SectionDisclosure>
                ) : null}

                {/* LEARN & HELP */}
                <SectionDisclosure id="process-mapping.home.learn-help" title={t('processMappingAssistant.sections.learnHelp')} defaultOpen={false} compact>
                  <button
                    type="button"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.open('https://support.creately.com/en/category/creately-ai-process-map-102yiu0/', '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className="w-full flex items-center justify-between p-2.5 rounded-md border bg-muted/40 hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    <span className="text-sm">{t('processMappingAssistant.learnHelp.learnHowToDraw')}</span>
                    <span className="text-xs text-muted-foreground">↗</span>
                  </button>
                </SectionDisclosure>
              </div>
            </div>
          )}
        </div>
      </div>
    </AssistantPanelShell>
    </>
  );
};
